import { MissingTranslationHandler, MissingTranslationHandlerParams } from '@ngx-translate/core';
import { VocabularyService } from '../server/services/vocabulary.service';
import { Subject, of, timer, retry } from 'rxjs';
import { concatMap, delay, catchError, tap } from 'rxjs/operators';

// Bump version when sessionStorage schema changes to force a clean reset
const SESSION_KEY = '_vocab_sent_v2';

export class CustomMissingTranslationHandler implements MissingTranslationHandler {
	// Static: shared across all instances (lazy modules create new instances)
	private static _sentKeys = new Set<string>(CustomMissingTranslationHandler._loadFromSession());

	private _missingQueue$ = new Subject<string>();

	private static _loadFromSession(): string[] {
		try {
			return JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? '[]');
		} catch {
			return [];
		}
	}

	private static _persistSuccess(key: string): void {
		// Persist only confirmed-created keys so failed/retried keys can be re-sent next session
		CustomMissingTranslationHandler._sentKeys.add(key);
		try {
			sessionStorage.setItem(SESSION_KEY, JSON.stringify([...CustomMissingTranslationHandler._sentKeys]));
		} catch {}
	}

	private static _removeFromPersisted(key: string): void {
		CustomMissingTranslationHandler._sentKeys.delete(key);
		try {
			sessionStorage.setItem(SESSION_KEY, JSON.stringify([...CustomMissingTranslationHandler._sentKeys]));
		} catch {}
	}

	constructor(private _vocabularyService: VocabularyService) {
		// Process one key at a time; 250ms gap to stay under CosmosDB rate limit
		this._missingQueue$
			.pipe(
				concatMap(key =>
					this._vocabularyService.createVocabulary(key).pipe(
						// Retry on 429 with exponential backoff (retryCount is 1-based)
						retry({
							count: 5,
							delay: (err: any, retryCount: number) => {
								if (err.status !== 429) throw err;
								console.warn(`Vocabulary 429 for "${key}", retry ${retryCount}/5`);
								return timer(Math.pow(2, retryCount - 1) * 1000);
							},
						}),
						tap(() => {
							// Persist only after confirmed success
							CustomMissingTranslationHandler._persistSuccess(key);
						}),
						catchError(err => {
							console.warn('Vocabulary creation failed for key:', key, err);
							// Remove from persisted set so next session can retry
							CustomMissingTranslationHandler._removeFromPersisted(key);
							return of(null);
						}),
						delay(250)
					)
				)
			)
			.subscribe();
	}

	handle(params: MissingTranslationHandlerParams) {
		const currentLang = params.translateService.store.currentLang;
		const translations = params.translateService.translations[currentLang];

		if (translations !== undefined) {
			const currentValue = translations[params.key];
			// Queue if missing OR if the value equals the key itself (was created with wrong fallback)
			const needsCreation = currentValue === undefined || currentValue === params.key;

			if (needsCreation && !CustomMissingTranslationHandler._sentKeys.has(params.key)) {
				// Add to in-memory set immediately to prevent duplicate queuing in this session
				CustomMissingTranslationHandler._sentKeys.add(params.key);
				this._missingQueue$.next(params.key);
				// Note: sessionStorage is updated only after successful API response (in tap/catchError)
			}
		}

		return params.key;
	}
}
